import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cameraErrorMessage, createBarcodeCamera } from '../utils/barcodeCamera';

const ZxingBarcodeScanner = ({ onScan, scanning, onCapture, captureLabel = '拍照並帶回表單' }) => {
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const onScanRef = useRef(onScan);
  const photoMode = Boolean(onCapture);
  onScanRef.current = onScan;
  const [retry, setRetry] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [scannerStatus, setScannerStatus] = useState('正在啟動掃描…');
  const [scanLine, setScanLine] = useState(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [captureReady, setCaptureReady] = useState(false);

  useEffect(() => {
    if (!scanning) return undefined;
    let session;
    const begin = () => {
      session?.stop();
      setErrorMessage('');
      setScanLine(null);
      setTorchSupported(false);
      setTorchEnabled(false);
      setCaptureReady(false);
      session = createBarcodeCamera({
        video: videoRef.current,
        detect: !photoMode,
        onStatus: setScannerStatus,
        onTrack: (track) => {
          setCaptureReady(true);
          const capabilities = track?.getCapabilities?.() || {};
          setTorchSupported(Boolean(capabilities.torch));
          if (capabilities.focusMode?.includes('continuous')) {
            track.applyConstraints?.({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
          }
        },
        onBarcode: (barcode) => {
          const video = videoRef.current;
          const box = barcode?.boundingBox;
          const preview = previewRef.current;
          if (!box || !video?.videoWidth || !preview) return;
          const scale = Math.max(preview.clientWidth / video.videoWidth, preview.clientHeight / video.videoHeight);
          setScanLine({ active: true, width: box.width * scale, angle: 0,
            centerX: (box.x + box.width / 2) * scale + (preview.clientWidth - video.videoWidth * scale) / 2,
            centerY: (box.y + box.height / 2) * scale + (preview.clientHeight - video.videoHeight * scale) / 2 });
        },
        onResult: (value) => {
          setScannerStatus(`已讀取條碼：${value}`);
          navigator.vibrate?.([60, 35, 60]);
          onScanRef.current(value);
        },
        onError: (error) => {
          setCaptureReady(false);
          const message = cameraErrorMessage(error);
          setErrorMessage(photoMode ? message.replaceAll('重新掃描', '重新開啟相機').replace('或直接輸入條碼', '或跳過拍照後填寫商品資料') : message);
          setScannerStatus('相機啟動失敗');
        },
      });
      session.start();
    };
    const visibilityChanged = () => {
      if (document.hidden) { session?.stop(); setScannerStatus('掃描已暫停'); }
      else begin();
    };
    if (!document.hidden) begin();
    document.addEventListener('visibilitychange', visibilityChanged);
    return () => { document.removeEventListener('visibilitychange', visibilityChanged); session?.stop(); };
  }, [scanning, retry, photoMode]);

  const toggleTorch = useCallback(async () => {
    const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
    if (!track?.applyConstraints) return;
    try { await track.applyConstraints({ advanced: [{ torch: !torchEnabled }] }); setTorchEnabled(!torchEnabled); }
    catch (_) { setTorchSupported(false); }
  }, [torchEnabled]);

  if (!scanning) {
    return null;
  }

  return (
    <div style={{ width: '100%' }}>
      <div
        ref={previewRef}
        style={{
          width: '100%',
          height: 'min(62vh, 380px)',
          minHeight: '280px',
          maxHeight: '380px',
          overflow: 'hidden',
          borderRadius: '18px',
          background: '#111',
          position: 'relative',
        }}
      >
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '6%',
            right: '6%',
            top: '50%',
            height: onCapture ? '75%' : '128px',
            transform: 'translateY(-50%)',
            border: '2px solid rgba(255,255,255,0.9)',
            borderRadius: '16px',
            boxShadow: '0 0 0 999px rgba(0,0,0,0.28)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '10%',
            right: '10%',
            top: '50%',
            height: '3px',
            borderRadius: '999px',
            background: 'rgba(34,197,94,0.9)',
            boxShadow: '0 0 12px rgba(34,197,94,0.65)',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            transition: 'opacity 0.15s ease',
            opacity: onCapture ? 0 : scanLine?.active ? 0.28 : 1,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '22%',
            bottom: '22%',
            left: '50%',
            width: '2px',
            borderRadius: '999px',
            background: 'rgba(255,255,255,0.45)',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            opacity: onCapture ? 0 : scanLine?.active ? 0.18 : 1,
          }}
        />
        {scanLine?.active && (
          <div
            style={{
              position: 'absolute',
              left: `${scanLine.centerX}px`,
              top: `${scanLine.centerY}px`,
              width: `${scanLine.width}px`,
              height: '4px',
              borderRadius: '999px',
              background: '#22c55e',
              boxShadow: '0 0 14px rgba(34,197,94,0.9)',
              transform: `translate(-50%, -50%) rotate(${scanLine.angle}deg)`,
              transformOrigin: 'center',
              pointerEvents: 'none',
              transition: 'left 0.12s ease, top 0.12s ease, width 0.12s ease, transform 0.12s ease',
            }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '14px',
            transform: 'translateX(-50%)',
            padding: '0.35rem 0.7rem',
            borderRadius: '999px',
            color: '#fff',
            background: 'rgba(15, 23, 42, 0.72)',
            fontSize: '0.78rem',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {onCapture ? '商品拍照' : '自動尋找條碼，直向也可掃'}
        </div>
      </div>
      {torchSupported && (
        <button
          type="button"
          onClick={toggleTorch}
          style={{
            width: '100%',
            marginTop: '0.65rem',
            border: '1px solid #dbeafe',
            borderRadius: '12px',
            padding: '0.72rem',
            background: torchEnabled ? '#2563eb' : '#eff6ff',
            color: torchEnabled ? '#fff' : '#1d4ed8',
            fontSize: '0.92rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {torchEnabled ? '關閉補光燈' : '開啟補光燈'}
        </button>
      )}
      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem', textAlign: 'center' }}>
        {scannerStatus}{!onCapture && '。請保持 15 到 25 公分，讓條碼填滿框線。'}
      </div>
      {onCapture && <button type="button" disabled={!captureReady} onClick={() => {
        const video = videoRef.current;
        if (!video?.videoWidth || !video?.videoHeight) return;
        try {
          const scale = Math.min(1, 900 / Math.max(video.videoWidth, video.videoHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
          canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
          onCapture(canvas.toDataURL('image/jpeg', 0.82));
        } catch (_) { setErrorMessage('拍照失敗，請重試或使用原本的選擇檔案。'); }
      }} style={{ width: '100%', marginTop: '0.75rem', padding: '0.85rem', background: '#333', color: '#fff', border: 0, borderRadius: '8px', fontSize: '1rem', opacity: captureReady ? 1 : 0.5 }}>{captureLabel}</button>}
      {errorMessage && (
        <div style={{ color: '#c2410c', fontSize: '0.9rem', marginTop: '0.5rem', textAlign: 'center' }}>
          {errorMessage}
          <button type="button" onClick={() => setRetry(value => value + 1)}>{photoMode ? '重新開啟相機' : '重新掃描'}</button>
        </div>
      )}
    </div>
  );
};

export default ZxingBarcodeScanner;
